import ENVIRONMENT_VARIABLES from '../constants/environmentVariables';
import type { BuildInfo, CognitoConfig, MonitoringConfig } from '../types/config/environment.types';

/**
 * Utility functions for accessing environment variables configured through CDK deployment-time replacements
 */

/**
 * Get all environment variables
 */
export const getEnvironmentVariables = (): typeof ENVIRONMENT_VARIABLES.env => ENVIRONMENT_VARIABLES.env;

/**
 * Get a specific environment variable
 */
export const getEnvironmentVariable = (key: keyof typeof ENVIRONMENT_VARIABLES.env): string => {
  return ENVIRONMENT_VARIABLES.env[key];
};

/**
 * Check if we're in development mode
 */
export const isDevelopment = (): boolean => {
  return getEnvironmentVariable('Stage') === 'dev';
};

/**
 * Check if we're in production mode
 */
export const isProduction = (): boolean => {
  return getEnvironmentVariable('Stage') === 'prod';
};

/**
 * Check if we're in alpha stage
 */
export const isAlpha = (): boolean => {
  return getEnvironmentVariable('Stage') === 'alpha';
};

/**
 * Get the current AWS region
 */
export const getAwsRegion = (): string => {
  return getEnvironmentVariable('Region');
};

/**
 * Get the current stage
 */
export const getStage = (): string => {
  return getEnvironmentVariable('Stage');
};

/**
 * Get the application domain from CDK environment configuration
 * This value is properly configured by CDK deployment-time replacements
 * and should match the portalDomain configured in environment-config.ts
 */
export const getApplicationDomain = (): string => {
  return getEnvironmentVariable('ApplicationDomain');
};

/**
 * Get the full application URL with protocol
 * Uses the properly configured application domain from CDK
 */
export const getApplicationUrl = (): string => {
  const domain = getApplicationDomain();

  // For localhost (development), use http
  if (domain.includes('localhost')) {
    return `http://${domain}`;
  }

  // For all deployed environments, use https
  return `https://${domain}`;
};

/**
 * Get the CloudFront domain (for cases where it's still needed)
 */
export const getCloudFrontDomain = (): string => {
  return getEnvironmentVariable('CloudFrontDomain');
};

/**
 * Get the CloudFront protocol
 */
export const getCloudFrontProtocol = (): string => {
  return getEnvironmentVariable('CloudFrontProtocol');
};

/**
 * Get the full CloudFront URL
 */
export const getCloudFrontUrl = (): string => {
  const protocol = getCloudFrontProtocol();
  const domain = getCloudFrontDomain();
  return `${protocol}://${domain}`;
};

/**
 * Get Cognito configuration
 */
export const getCognitoConfig = (): CognitoConfig => ({
  userPoolId: getEnvironmentVariable('CognitoUserPoolId'),
  userPoolWebClientId: getEnvironmentVariable('CognitoUserPoolWebClientId'),
  identityPoolId: getEnvironmentVariable('CognitoIdentityPoolId'),
  domain: getEnvironmentVariable('CognitoDomain'),
  authRoleARN: getEnvironmentVariable('AuthRoleARN'),
  unAuthRoleARN: getEnvironmentVariable('UnAuthRoleARN'),
});

/**
 * Get monitoring configuration
 */
export const getMonitoringConfig = (): MonitoringConfig => ({
  rumAppMonitorId: getEnvironmentVariable('RumAppMonitorId'),
  loggerUrl: getEnvironmentVariable('LoggerUrl'),
  metricsUrl: getEnvironmentVariable('MetricsUrl'),
});

/**
 * Get build information
 */
export const getBuildInfo = (): BuildInfo => ({
  buildTime: getEnvironmentVariable('BuildTime'),
  stage: getEnvironmentVariable('Stage'),
  region: getEnvironmentVariable('Region'),
  awsAccountId: getEnvironmentVariable('AwsAccountId'),
});
